import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function GroupBenefitsPolicies() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="group-benefits-policies" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Group Benefits Policies</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Attract, Retain, and Protect Your Team with Comprehensive Group Plans
            </Text>
          </div>

          <div className="space-y-8">
            {/* What are Group Benefits? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What are Group Benefits Policies?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Group benefits are insurance plans offered by an employer or association (the plan sponsor) to its employees or members (the plan members). These plans provide a package of coverages designed to protect the health, well-being, and financial security of the group members and often their dependents.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Common coverages include extended health care (prescription drugs, paramedical services), dental care, vision care, life insurance, accidental death & dismemberment (AD&D), short-term disability (STD), and long-term disability (LTD). Plans are customizable to meet the specific needs and budget of the sponsoring organization.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How Group Benefits Work</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Plan Design & Consultation:</strong> We work with the employer/association to understand their goals, budget, and workforce demographics to design a suitable benefits package.
                </li>
                <li>
                  <strong>Quoting & Carrier Selection:</strong> We obtain quotes from various insurance carriers and help select the best provider based on coverage, cost, and service.
                </li>
                <li>
                  <strong>Implementation & Enrollment:</strong> We assist with setting up the plan and enrolling eligible employees/members.
                </li>
                <li>
                  <strong>Premium Payment:</strong> Premiums are typically shared between the employer and employees (cost-sharing) or fully paid by the employer. Premiums are paid regularly to the insurance carrier.
                </li>
                <li>
                  <strong>Claims Processing:</strong> When a plan member needs to use a benefit (e.g., visit the dentist, fill a prescription, go on disability leave), they submit a claim to the insurance carrier for reimbursement or direct payment according to the plan terms.
                </li>
                 <li>
                  <strong>Ongoing Management & Review:</strong> We provide ongoing support, help with plan administration, and conduct regular reviews (usually annually) to ensure the plan remains competitive and meets the organization's evolving needs.
                </li>
              </ol>
            </Card>

            {/* Key Benefits for Employers & Employees */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">For Employers:</h4>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                    <li><strong>Attract & Retain Talent:</strong> A strong benefits package is crucial for competing for skilled employees.</li>
                    <li><strong>Improve Morale & Productivity:</strong> Shows employees they are valued, potentially reducing absenteeism.</li>
                    <li><strong>Tax Advantages:</strong> Employer-paid premiums are generally a tax-deductible business expense.</li>
                    <li><strong>Cost Management:</strong> Group rates are typically lower than individual insurance costs.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">For Employees:</h4>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                    <li><strong>Affordable Access:</strong> Provides access to valuable health, dental, and insurance coverage at lower group rates.</li>
                    <li><strong>Financial Security:</strong> Protects against unexpected health costs and loss of income due to disability.</li>
                    <li><strong>Simplified Underwriting:</strong> Often requires less medical information than individual policies.</li>
                    <li><strong>Peace of Mind:</strong> Offers security for employees and their families.</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations for Plan Sponsors</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Factors for employers/associations to consider:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Cost & Budgeting:</strong> Balancing desired coverage levels with affordability. Premiums can increase at renewal based on claims experience and inflation.</li>
                <li><strong>Plan Design Flexibility:</strong> Choosing the right mix of mandatory and optional benefits.</li>
                <li><strong>Participation Requirements:</strong> Minimum number of employees often required.</li>
                <li><strong>Administration:</strong> Requires ongoing effort for enrollment, terminations, and updates.</li>
                <li><strong>Employee Communication:</strong> Clearly explaining the benefits package to ensure employees understand and value it.</li>
                <li><strong>Sustainability:</strong> Designing a plan that can be maintained long-term.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Are Group Benefits Right for Your Organization?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Group benefits plans are essential for most businesses and associations that want to:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Compete effectively for talent in their industry.</li>
                <li>Provide financial security and health support for their employees/members.</li>
                <li>Foster a positive and productive work environment.</li>
                <li>Manage risks associated with employee health and disability.</li>
                <li>Offer a valuable component of total compensation.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Invest in Your Team's Well-being</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Let us help you design and implement a group benefits plan that aligns with your organization's goals and budget, providing valuable protection for your members.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Request a Group Benefits Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
